type LogChannel = 'analytics' | 'audio' | 'db' | 'game' | 'store' | 'system' | 'test' | 'ui';
type LogLevel = 'error' | 'warn' | 'info' | 'debug';

const DEFAULT_LEVEL: LogLevel = 'info';

const CHANNEL_LEVELS: Partial<Record<LogChannel, LogLevel>> = {
};

const LEVEL_PRIORITY: Record<LogLevel, number> = {
	error: 0,
	warn: 1,
	info: 2,
	debug: 3,
};

/** A minimal logger interface */
type Logger = {
	error: (message: string, ...args: unknown[]) => void;
	warn: (message: string, ...args: unknown[]) => void;
	info: (message: string, ...args: unknown[]) => void;
	debug: (message: string, ...args: unknown[]) => void;
};

function createConsoleLogger(channel: LogChannel): Logger {
	const prefix = `[${channel}]`;
	const levelThreshold = CHANNEL_LEVELS[channel] ?? DEFAULT_LEVEL;
	const maxPriority = LEVEL_PRIORITY[levelThreshold];

	function shouldLog(level: LogLevel): boolean {
		return LEVEL_PRIORITY[level] <= maxPriority;
	}

	/* eslint-disable no-console */
	return {
		error: (message: string, ...args: unknown[]) => {
			if (shouldLog('error')) {
				console.error(prefix, message, ...args);
			}
		},
		warn: (message: string, ...args: unknown[]) => {
			if (shouldLog('warn')) {
				console.warn(prefix, message, ...args);
			}
		},
		info: (message: string, ...args: unknown[]) => {
			if (shouldLog('info')) {
				console.info(prefix, message, ...args);
			}
		},
		debug: (message: string, ...args: unknown[]) => {
			if (shouldLog('debug')) {
				console.debug(prefix, message, ...args);
			}
		},
	};
	/* eslint-enable no-console */
}

const factory = createConsoleLogger;

export const Log: Record<LogChannel, Logger> = {
	analytics: factory('analytics'),
	audio: factory('audio'),
	db: factory('db'),
	game: factory('game'),
	store: factory('store'),
	system: factory('system'),
	test: factory('test'),
	ui: factory('ui'),
};
